import React from 'react';
import { useTranslation } from 'react-i18next';
import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import ClinicSelectBox from 'components/molecules/clinic-select-box';
import { ClinicStatus } from 'infrastructure/enums';
import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import DoNotCallRestrictionTypeSelectBox from 'components/molecules/do-not-call-restriction-type-select-box';

import s from './styles.module.scss';

import type { TNormalReadingsFilter } from 'infrastructure/hooks/normal-reading-report';

interface IProps {
  filter: TNormalReadingsFilter;
  updateFilter: (newFilter: Partial<TNormalReadingsFilter>) => void;
  resetFilter: () => void;
  count: number;
}

const ExpandableNormalReadingsReportFilters: React.FC<IProps> = (props) => {
  const { filter, count, updateFilter, resetFilter } = props;
  const { t } = useTranslation();

  const onCalendarChange = ([startDate, endDate]: [
    Date | null,
    Date | null,
  ]) => {
    if (startDate === null && endDate === null) {
      updateFilter({
        startDate: undefined,
        endDate: undefined,
      });
      return;
    }

    updateFilter({
      startDate: startDate?.getTime(),
      endDate: endDate?.getTime(),
    });
  };

  return (
    <ExpandableFilterPanel
      filtersCount={count}
      onReset={resetFilter}
      openWithValue
      isExpanded
    >
      <div className={s.wrapper}>
        <ExpandableFilterPanelItem label={t('labels.reportTimeWindow')}>
          <BaseRangeDatepicker
            onCalendarChange={onCalendarChange}
            value={[new Date(filter.startDate), new Date(filter.endDate)]}
            withPresets
            disabledDate={(current) => {
              return current && current > new Date();
            }}
            isMaxDayToday
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.interactionStatus')}>
          <BooleanSelectBox
            allowClear
            withAll
            value={filter.interactionStatus}
            onChange={(value) => {
              const interactionStatus =
                typeof value === 'boolean' ? String(value) : value;
              updateFilter({ interactionStatus, page: undefined });
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.doNotCall')}>
          <DoNotCallRestrictionTypeSelectBox
            allowClear
            withNull
            value={filter.doNotCallRestrictionType}
            onChange={(doNotCallRestrictionType) => {
              updateFilter({ doNotCallRestrictionType, page: undefined });
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.clinic')}>
          <ClinicSelectBox
            allowClear
            status={[ClinicStatus.Active]}
            value={filter.clinicGuid}
            onChange={(guid) => {
              if (guid) {
                updateFilter({
                  clinicGuid: guid,
                  page: undefined,
                });
              } else {
                updateFilter({
                  clinicGuid: undefined,
                  page: undefined,
                });
              }
            }}
          />
        </ExpandableFilterPanelItem>
      </div>
    </ExpandableFilterPanel>
  );
};

export default ExpandableNormalReadingsReportFilters;
